import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { publicScheduleService } from '@/services/PublicScheduleService'
import { publicScheduleKeys } from '@/hooks/queryKeys'
import type { PublicEventDetailDto, PublicScheduleDto, VenueShareLinkStateDto } from '@lib/dto/public-schedule.dto'

/** The venue's live share link. Admin-only, so `enabled` guards a 403. */
export function useVenueShareLink(venueId: string, enabled = true): UseQueryResult<VenueShareLinkStateDto> {
  return useQuery({
    queryKey: publicScheduleKeys.link(venueId),
    queryFn: () => publicScheduleService.shareLink(venueId),
    staleTime: 60_000,
    enabled,
  })
}

export function useReplaceVenueShareLink(venueId: string): UseMutationResult<VenueShareLinkStateDto, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => publicScheduleService.replaceShareLink(venueId),
    onSuccess: (state) => queryClient.setQueryData(publicScheduleKeys.link(venueId), state),
  })
}

/**
 * The crew's list.
 *
 * `retry: false` because the failures here are answers, not blips: a revoked
 * link and an unknown one both 404, and retrying three times only delays the
 * page that explains it. Refetched on focus, since this is a bookmark somebody
 * comes back to across a day and the list moves under them.
 */
export function usePublicSchedule(token: string): UseQueryResult<PublicScheduleDto> {
  return useQuery({
    queryKey: publicScheduleKeys.schedule(token),
    queryFn: () => publicScheduleService.schedule(token),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function usePublicEvent(token: string, eventId: string): UseQueryResult<PublicEventDetailDto> {
  return useQuery({
    queryKey: publicScheduleKeys.event(token, eventId),
    queryFn: () => publicScheduleService.event(token, eventId),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })
}
